import { Component, ElementRef, viewChild, AfterViewInit, OnDestroy, NgZone, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MarkdownModule } from 'ngx-markdown';
import { environment } from '../../environments/environment';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

@Component({
  selector: 'app-child-scene',
  standalone: true,
  imports: [CommonModule, MarkdownModule],
  template: `
    <div class="child-container" #container>
      <canvas #childCanvas class="child-canvas" (click)="onCanvasClick($event)"></canvas>
      <div #labelRendererContainer class="label-renderer-container"></div>

      <!-- Modal for selected news item with markdown description -->
      @if (selectedNews()) {
        <div class="news-modal">
          <div class="modal-content">
            <h3>{{ selectedNews().title || 'News Details' }}</h3>
            <div class="modal-body">
              <markdown [data]="selectedNews().description || selectedNews().content || 'No details available.'"></markdown>
            </div>
            <button (click)="closeModal()">Close</button>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .child-container {
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;
    }
    .child-canvas {
      width: 100%;
      height: 100%;
      display: block;
      cursor: pointer;
    }
    .label-renderer-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none; /* Allows click events to pass through to canvas */
      overflow: hidden;
    }
    ::ng-deep .news-label {
      background: rgba(0, 0, 0, 0.75);
      color: #ffffff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-family: sans-serif;
      white-space: nowrap;
      pointer-events: none;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      margin-top: -30px; /* Offset to push label above the object */
      transition: opacity 0.2s ease;
    }
    .news-modal {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 100;
    }
    .modal-content {
      background: white;
      padding: 24px;
      border-radius: 8px;
      max-width: 500px;
      width: 90%;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 4px 15px rgba(0,0,0,0.2);
      font-family: sans-serif;
    }
    .modal-content h3 {
      margin-top: 0;
      color: #333;
    }
    .modal-body {
      margin: 15px 0;
      color: #444;
      line-height: 1.5;
    }
    .modal-content button {
      background: #007bff;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      margin-top: 10px;
    }
  `]
})
export class ChildScene implements AfterViewInit, OnDestroy {
  private canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('childCanvas');
  private containerRef = viewChild.required<ElementRef<HTMLDivElement>>('container');
  private labelRendererContainerRef = viewChild.required<ElementRef<HTMLDivElement>>('labelRendererContainer');
  
  private ngZone = inject(NgZone);
  private http = inject(HttpClient);

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private labelRenderer!: CSS2DRenderer;
  private controls!: OrbitControls;
  private animationFrameId!: number;
  private resizeObserver!: ResizeObserver;

  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private newsMeshes: THREE.Object3D[] = [];

  // Signal for the selected modal content
  selectedNews = signal<any | null>(null);

  ngAfterViewInit(): void {
    if (typeof window !== 'undefined') {
      this.initThree();
      this.fetchNewsAndCreateObjects();

      this.ngZone.runOutsideAngular(() => {
        this.animate();
      });
    }
  }

  private initThree(): void {
    const canvas = this.canvasRef().nativeElement;
    const container = this.containerRef().nativeElement;
    const labelContainer = this.labelRendererContainerRef().nativeElement;
    const width = container.clientWidth || 300;
    const height = container.clientHeight || 300;

    // 1. Scene setup with color background
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a2e);

    // 2. Camera positioned for a top-down/isometric view of the horizontal circular layout
    this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    this.camera.position.set(0, 2, 2);

    // 3. Lighting & Shadows configuration
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xfff5e6, 1.5);
    directionalLight.position.set(5, 10, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    directionalLight.shadow.bias = -0.0001;
    this.scene.add(directionalLight);

    const pointLight = new THREE.PointLight(0x00d2fc, 2, 10);
    pointLight.position.set(-4, 3, 2);
    this.scene.add(pointLight);

    // 4. Renderer setup with shadows enabled
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // 4.1 CSS2D Label Renderer setup
    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.setSize(width, height);
    this.labelRenderer.domElement.style.position = 'absolute';
    this.labelRenderer.domElement.style.top = '0px';
    this.labelRenderer.domElement.style.pointerEvents = 'none';
    labelContainer.appendChild(this.labelRenderer.domElement);

    // 5. Add horizontal floor plane (X-Z plane) to receive shadows
    const planeGeometry = new THREE.PlaneGeometry(30, 30);
    const planeMaterial = new THREE.MeshStandardMaterial({
      color: 0x16213e,
      roughness: 0.8,
      metalness: 0.2
    });
    const floor = new THREE.Mesh(planeGeometry, planeMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.5;
    floor.receiveShadow = true;
    this.scene.add(floor);

    // 6. OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;

    // 7. Resize Observer for Container Changes
    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          this.camera.aspect = width / height;
          this.camera.updateProjectionMatrix();
          this.renderer.setSize(width, height);
          this.labelRenderer.setSize(width, height);
        }
      }
    });
    this.resizeObserver.observe(container);
  }

  private fetchNewsAndCreateObjects(): void {
    const loader = new GLTFLoader();

    // 1. Load and place the single central model first
    loader.load(
      'new.glb',
      (gltf) => {
        const centerModel = gltf.scene.clone();
        centerModel.position.set(0, 0, 0);
        centerModel.scale.set(5, 5, 5);

        centerModel.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            if ((child as THREE.Mesh).material) {
              const material = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
              if (material.isMeshStandardMaterial) {
                material.roughness = 0.3;
                material.metalness = 0.4;
              }
            }
          }
        });

        this.scene.add(centerModel);
        this.newsMeshes.push(centerModel);
      },
      undefined,
      (error) => {
        console.error('Error loading central new.glb model:', error);
      }
    );

    // 2. Fetch the remaining news items and layout them around in a circle
    this.http.get<any[]>(`${environment.apiURL}/api/links/news`).subscribe({
      next: (newsItems) => {
        if (!Array.isArray(newsItems)) return;

        loader.load(
          'new.glb',
          (gltf) => {
            const totalItems = newsItems.length;

            newsItems.forEach((news, index) => {
              const modelClone = gltf.scene.clone();

              const angle = (index / totalItems) * Math.PI * 2;
              const radius = 3.5;
              modelClone.position.x = Math.cos(angle) * radius;
              modelClone.position.y = 0;
              modelClone.position.z = Math.sin(angle) * radius;

              modelClone.scale.set(1, 1, 1);

              modelClone.traverse((child) => {
                child.userData = { newsData: news };
                if ((child as THREE.Mesh).isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;

                  if ((child as THREE.Mesh).material) {
                    const material = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
                    if (material.isMeshStandardMaterial) {
                      material.roughness = 0.2;
                      material.metalness = 0.1;
                    }
                  }
                }
              });

              // Create and attach a CSS2D label title to the model
              const titleText = news.title || `News ${index + 1}`;
              const p = document.createElement('div');
              p.className = 'news-label';
              p.textContent = titleText;
              
              const cDiv = new CSS2DObject(p);
              cDiv.position.set(0, 1.2, 0); // Position slightly above the object
              modelClone.add(cDiv);

              this.scene.add(modelClone);
              this.newsMeshes.push(modelClone);
            });
          },
          undefined,
          (error) => {
            console.error('Error loading surrounding new.glb models:', error);
            this.createFallbackObjects(newsItems);
          }
        );
      },
      error: (err) => {
        console.error('Failed to load news items from API', err);
        const fallbackNews = [{
          title: 'Default News',
          description: 'API could not be reached. **Please check your connection.**'
        }];
        this.createFallbackObjects(fallbackNews);
      }
    });
  }

  private createFallbackObjects(newsItems: any[]): void {
    const geometry = new THREE.BoxGeometry(0.8, 0.8, 0.8);
    const totalItems = newsItems.length;

    newsItems.forEach((news, index) => {
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(`hsl(${(index * 360) / totalItems}, 80%, 60%)`),
        roughness: 0.2,
        metalness: 0.1
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      const angle = (index / totalItems) * Math.PI * 2;
      const radius = 3.5;
      mesh.position.x = Math.cos(angle) * radius;
      mesh.position.y = 0;
      mesh.position.z = Math.sin(angle) * radius;

      mesh.userData = { newsData: news };

      // Attach label for fallback objects as well
      const titleText = news.title || `News ${index + 1}`;
      const p = document.createElement('div');
      p.className = 'news-label';
      p.textContent = titleText;
      
      const cDiv = new CSS2DObject(p);
      cDiv.position.set(0, 0.8, 0);
      mesh.add(cDiv);

      this.scene.add(mesh);
      this.newsMeshes.push(mesh);
    });
  }

  public onCanvasClick(event: MouseEvent): void {
    const canvas = this.canvasRef().nativeElement;
    const rect = canvas.getBoundingClientRect();

    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.newsMeshes, true);

    if (intersects.length > 0) {
      let currentObj: THREE.Object3D | null = intersects[0].object;
      let newsData = null;

      while (currentObj) {
        if (currentObj.userData && currentObj.userData['newsData']) {
          newsData = currentObj.userData['newsData'];
          break;
        }
        currentObj = currentObj.parent;
      }

      if (newsData) {
        this.ngZone.run(() => {
          this.selectedNews.set(newsData);
        });
      }
    }
  }

  closeModal(): void {
    this.selectedNews.set(null);
  }

  private animate = (): void => {
    this.animationFrameId = requestAnimationFrame(this.animate);

    // Rotate all loaded GLTF models gently while preserving label alignment
    this.newsMeshes.forEach((mesh, index) => {
      mesh.rotation.x += 0.005 * (index + 1);
      mesh.rotation.y += 0.01 * (index + 1);
    });

    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    this.labelRenderer.render(this.scene, this.camera);
  }

  ngOnDestroy(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.controls?.dispose();
    this.renderer?.dispose();
  }
}
